/**
 * Scribe Event Constants
 *
 * This module centralizes the types of events supported by the scribe client
 * API.
 *
 * @module    scribe_event_constants
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
const scribeEventConstants = {
  /**
   * This event is sent at a regular interval to track that a user is
   * still browsing a page.  The payload of the event is general performance
   * metrics for Scribe's client library.
   *
   * @example
   * {
   *   eventType: 'Heartbeat',
   *   retuestsMade: 12,
   *   requestsFailed: 1,
   *   requestsCompleted: 11,
   *   meanResponseTimeInMs: 88
   * }
   */
  HEARTBEAT: 'Heartbeat',
  /**
   * The user changed tabs in a browser.
   *
   * @example
   * {
   *   eventType: 'PageHidden',
   * }
   */
  PAGE_HIDDEN: 'PageHidden',
  /**
   * Shows what prices the customer is viwing on the page (currently PDP pages for now)
   *
   * @example
   * {
   *  eventType: 'Pricing',
   * }
   */
  PRICING: 'Pricing',
  /**
   * A page was viewed in a browser
   *
   * @example
   * {
   *   eventType: 'PageView',
   * }
   */
  PAGE_VIEW: 'PageView',
  /**
   * A page was prerendered in a browser
   *
   * @example
   * {
   *   eventType: 'Prerender',
   * }
   */
  PRERENDER: 'Prerender',
  /**
   * Click event that is dispatched to Scribe Asynchronously
   * using ScribeClient
   *
   * @example
   * {
   *   eventType: 'WebClick',
   *   clickLocation: 'my_link',
   *   clickLocationMetadata: 'my_optional_metadata', // optional
   *   cmsLinkId: '901468_tn_furniture_DeptHeader',   // optional
   *   cmsLegoId: 343,                                // optional
   *   cmsLegoVersionId: 999123,                      // optional
   *   cmsPageId: 44356,                              // optional
   *   transactionId: <transaction id>,
   *   timestamp: <client timestamp>
   * }
   */
  WEB_CLICK: 'WebClick',
  /**
   * Libra Action event in javascript
   *
   * @example
   * {
   *   eventType: 'LibraAction',
   *   libraGuid: <some guid>,
   *   testGroupGuid: <some other guid>
   * }
   */
  LIBRA_ACTION: 'LibraAction',
  /**
   * All customer web actions
   *
   * @example
   * {
   *    eventType:WebAction
   *    actionName:review_pagination
   *    actionData:{"page_to_be_displayed":"1"}
   *    transactionId:ChgHDVf+r+V4nGWiBICFAg==
   *    storeId:49
   *    timestamp:1476309614703
   * }
   *
   */
  WEB_ACTION: 'WebAction',
  /**
   * Track user filtering activity on browse pages
   *
   * @example
   * {
   *    eventType: AttributeAction
   *    attributeId: 12345
   *    attributeValue: "Metal"
   *    attributePosition: 32
   *    attributeAction: "checked"
   * }
   */
  ATTRIBUTE_ACTION: 'AttributeAction',
  /**
   * Track when the user sees a class as a suggestion after a keyword search
   *
   * @example
   * {
   *    eventType: ClassSuggestionDisplayed
   *    classId: 4757
   *    className: Wall Posters
   *    classifierConfidenceScore: 0.6
   *    originalSearchTerm: paris poster
   * }
   *
   */
  CLASS_SUGGESTION_DISPLAYED: 'ClassSuggestionDisplayed',
  /**
   * Track third party activity on the site
   *
   * @example
   * {
   *    eventType:MalwareDetected
   *    actionName:malware_detected
   *    actionData:{"bot":"evil"}
   *    transactionId:ChgHDVf+r+V4nGWiBICFAg==
   *    storeId:49
   *    timestamp:1476309614703
   * }
   *
   */
  MALWARE_DETECTED: 'MalwareDetected',
  /**
   * Impression Event in javascript
   *
   * @example
   * {
   *   eventType: 'Impression',
   *   v: <some vendor>
   * }
   */
  IMPRESSION: 'Impression',
  /**
   * SiteImpressionEvent; when an impression is made by content served on Wayfair itself.
   *
   * @example
   * {
   *    eventType: 'SiteImpression',
   *    adType: 'cards',
   *    content: 'someContent',
   *    placementId: 0
   * }
   */
  SITE_IMPRESSION: 'SiteImpression',
  /**
   * Used to track clicks across admin, starting initially with the order wizards tool
   *
   * @example
   * {
   *    eventType: 'AdminOrderWizardClick',
   *    orderId: 12345
   *    customerId: 34567
   *    currentWizardId: 1
   *    currentUberWizardStep: 1
   *    clickLocation: 'reason'
   * }
   */
  ADMIN_ORDER_WIZARD_CLICK: 'AdminOrderWizardClick',
  /**
   * AdminDpnCallerIdentityEvent used by the Identify Caller page.
   *
   * @example
   * {
   *   eventType:           'AdminDpnCallerIdentity',
   *   dpn:                 '1001001001'
   *   customerPhoneNumber: '7007007000'
   *   dpnCallSessionId:    14082313406650964
   *   chosenAction:        'callerSelection'
   *   deviceGuid:          '3dcd51da-ab46-45e4-bc2b-2fdfa395b427' // optional
   *   lastTimeOnSite:      14082313406650964                      // optional
   *   customerId:          4979743649027291021                    // optional
   * }
   */
  ADMIN_DPN_CALLER_IDENTITY: 'AdminDpnCallerIdentity',
  /**
   * ContactUsEvent triggered from customer action on the Contact Us page.
   *
   * @example
   * {
   *   triggerAction  'deflectionChange'
   *   deflectionType '2'
   *   topicId        '1370'
   *   topicIds       '1370, 1284'
   *   sku            'CHLH1518'
   *   searchKey      '2443651784'
   *   tabId          'feedback'
   * }
   */
  CONTACT_US: 'ContactUs',
  /**
   * DesignerServicesCustomerFeedback indicates when a customer in a designer services program sends a feedback message.
   *
   * @example
   * {
   *   eventType: 'DesignerServicesCustomerFeedback'
   *   message: 'Test message'
   *   messageId: 839928289
   *   conversationId: 948489329
   *   designerId: 948482929
   *   customerId: 964938335
   *   source: 'chat'
   * }
   */
  DESIGNER_SERVICES_CUSTOMER_FEEDBACK: 'DesignerServicesCustomerFeedback',
  /**
   * DesignerFeedback indicates when a designer in a designer services program sends a feedback message.
   *
   * @example
   * {
   *   eventType: 'DesignerFeedback'
   *   message: 'Test message'
   *   messageId: 839928289
   *   conversationId: 948489329
   *   designerId: 948482929
   *   customerId: 964938335
   *   source: 'chat'
   * }
   */
  DESIGNER_FEEDBACK: 'DesignerFeedback',
  /**
   * ProductContentEvent triggered from media content load for a product on PDP.
   *
   * @example
   * {
   *   productSku              'CHLH1518'
   *   displaySku              'W517290291'
   *   layoutContent           '[2:28,128,266,930,1122,15997, 3:21,323,433]'
   *   mainCarouselResourceIds '[21767, 21425]'
   *   waymoreResourceIds      '[21467, 21478, 21480, 21472, 21490, 21477]'
   *   serviceResourceIds      '[27667, 21424]'
   * }
   */ PRODUCT_CONTENT: 'ProductContent',
  /**
   * SandboxEvent used to rapidly prototype sending events to Scribe.
   *
   * @example
   * {
   *   eventType:            'Sandbox',
   *   name:                 'TestEventName'
   *   <variable field key>: 'fieldValue1'   // optional, any number of additional fields
   * }
   */
  SANDBOX: 'Sandbox',
  /**
   * Used to track clicks on ads served externally by providers such as AppNexus
   *
   * @example
   * {
   *    eventType: 'AdClick'
   *    creativeId: 12345
   *    placementId: 67890
   * }
   */
  AD_CLICK: 'AdClick',
  /**
   * Used to track each first time an ad is displayed on screen
   *
   * @example
   * {
   *    eventType: 'AdImpression'
   *    creativeId: 12345
   *    placementId: 67890
   * }
   */
  AD_IMPRESSION: 'AdImpression',
  /**
   * Used to track submissions of escalations on the Order Wizard's dispute section
   *
   * @example
   * {
   *    eventType: 'CustomerEscalation',
   *    isCustomerAdvocacyEscalation: true,
   *    isManagerEscalation: true,
   *    primaryAdvocacyEscalationReason: 'Inquiries & Feedback',
   *    secondaryAdvocacyEscalationReason: 'Service - when will item ship/ status',
   *    primaryManagerEscalationReason: 'Other',
   *    secondarManagerEscalationReason: null,
   *    customerId: 4137340673,
   *    customerGuid: D260E770-C2A4-4018-9940-A410FF9B8074,
   *    orderId: 2566332713
   *    storeId: 49
   * }
   */
  CUSTOMER_ESCALATION: 'CustomerEscalation',
  /**
   * Track what options the customer selects on PDP page
   *
   * @example
   * {
   *     'price': 3.23,
   *     'sku': ABC1234,
   *     'optionId': [4234231, 232341],
   *     'source': 'PDP',
   *     'eventType': 'SelectOption'
   *  }
   */
  SELECT_OPTION: 'SelectOption',
  /**
   * Track what items on a page have been viewed by the customer
   *
   * @example
   * {
   *     'pageType': 'STLLandingPage',
   *     'photoIds': [3672678, 3988736]
   *     'positions': ['0-0','0-1']
   *  }
   *
   * @example
   * {
   *     'pageType': 'Homepage','HP_CARD_VP_']
   *     'positions': ['0', '1']
   *  }
   */
  IN_VIEW: 'InView',
  /**
   * Track what items on a browse page have been viewed by the customer
   *
   * @example
   * {
   *     'pageType': 'SuperbrowseCategory1Attributes',
   *     'displaySkus': ['C45982', 'B45982', 'C16982', 'A75130', 'D75230', 'L75132', null]
   *     'contentIds': [null, null, null, null, null, null, 'PAGINATION']
   *     'positions': ['0-0', '0-1', '0-2', '1-0', '1-1', '1-2', '1']
   *  }
   */
  BROWSE_IN_VIEW: 'BrowseInView',
  /**
   * Track what sponsored products are displayed to a user on browse pages
   *
   * @example
   * {
   *     'msptbs': 3.23,
   *     'sku': ABC1234,
   *     'placementId': 0,
   *     'slotId': 1,
   *     'position': 2,
   *     'campaignId': 4,
   *     'displayPrice': 1889.99,
   *     'categoryId': 12345,
   *     'referringTransactionId': abcd123
   *     'eventType': 'SponsoredProductImpression'
   *  }
   */
  SPONSORED_PRODUCT_IMPRESSION: 'SponsoredProductImpression',
  /**
   * Track what creative and products are displayed to a user by the Head of the Class media on browse pages
   *
   * @example
   * {
   *     'displaySkus': ["ABC1234", "ABC2345", "XYZ9876"],
   *     'placementId': 0,
   *     'creativeId': 12345,
   *     'eventType': 'HeadOfClassImpression'
   *  }
   */
  HEAD_OF_CLASS_IMPRESSION: 'HeadOfClassImpression',
  /**
   * Track what payment methods were shown to the user at the time of checkout
   *
   * @example
   * {
   *    'eventType: BillingPaymentOptions'
   *    'defaultPaymentMethodId': 1
   *    'paymentMethodIds': [1, 1, 7, 35]
   *    'availablePaymentMethods': ["paymentMethodName:MasterCard, paymentMethodName:Visa, paymentMethodName:PayPal, paymentMethodName:ChargeAfter"]
   *    'source': 'Checkout'
   * }
   */
  BILLING_PAYMENT_OPTIONS: 'BillingPaymentOptions',
  /**
   * Track which pixels actually fired on frontend
   *
   * Fields:
   * referringTransactionId - transaction id of the page request that generated the page
   * pixelIds - array of nteger - The identifier for the pixel that loaded. (Non-zero for Argus pixels, zero for Angus pixels) - [required]
   * argusActions - array of string - The description of the page or action defined in argus for this pixel, corresponds to values from PHP class Campaign_Management/Constants/Argus_Actions. - [required]
   * isVendorScript - array of boolean - Whether this pixel is a vendor script pixel. (Only applicable to Argus pixels, pixelId non-zero) - [required]
   */
  PIXEL_ACTIVITY: 'PixelActivity',
  /**
   * Track Page Views that occur on Wayfair Service pages
   *
   * * @example
   * {
   *   eventType: 'ServicePageView',
   * }
   *
   */
  SERVICE_PAGE_VIEW: 'ServicePageView',
  /**
   * Used to provide detailed session logging on pageload of TrackPackage2018
   *
   * @example
   * {
   *    eventType: 'TrackPackageRequest'
   *    orderID: 2821779251
   *    purchaseOrderNumber: 152357623
   *    orderProductId: 3438634301
   *    productStatus: 'IN_TRANSIT'
   * }
   */
  TRACK_PACKAGE: 'TrackPackage',
  ATLAS_IMPRESSION: 'AtlasImpression',
};

export default scribeEventConstants;
